import { Form, Formik } from 'formik';
import React from 'react';

import AutoSave from '@components/AutoSave';
import CheckBoxField from '@components/Form/CheckBoxField';
import { useCurrentUser, useEmailSettingsUpdate } from '@hooks';

import schema from './schema';
import styles from './styles.module.scss';

interface NotificationOptionType {
  name: string;
  title: string;
  description: string;
  isSubscribed: boolean;
  access: boolean;
}

const NOTIFICATION_OPTIONS: NotificationOptionType[] = [
  {
    name: 'commentNotificationEnabled',
    title: 'Comments',
    description:
      'Receive notifications of responses, mentions, and likes to forum posts and articles you’ve commented on.',
    isSubscribed: false,
    access: true,
  },
  {
    name: 'sendPromotionEmails',
    title: 'Promotions',
    description:
      'Stay up to date on special offers and more. Includes our new subscriber welcome emails.',
    isSubscribed: false,
    access: true,
  },
  {
    name: 'sendEventEmails',
    title: 'Events',
    description: 'Stay up to date on upcoming events.',
    isSubscribed: false,
    access: true,
  },
  {
    name: 'sendProductEmails',
    title: 'Product Updates',
    description: 'Receive an email when there are important product updates.',
    isSubscribed: false,
    access: true,
  },
  {
    name: 'surveyEnabled',
    title: 'Subscriber Survey',
    description: 'Participate in our subscriber survey.',
    isSubscribed: false,
    access: true,
  },
  {
    name: 'sendForumDigest',
    title: 'Forum Digest',
    description: 'Get a summary of subscriber forum posts.',
    isSubscribed: false,
    access: true,
  },
];

const SUBSCRIBER_NOTIFICATIONS_ONLY = ['commentNotificationEnabled', 'surveyEnabled'];

const NotificationSettings = ({ checkForConfirmationModal, userEmailSettings }) => {
  const { currentUser } = useCurrentUser();
  const { mutate: updateEmailSettings } = useEmailSettingsUpdate();

  NOTIFICATION_OPTIONS.forEach((option) => {
    option.isSubscribed = userEmailSettings[option.name];
    if (SUBSCRIBER_NOTIFICATIONS_ONLY.includes(option.name)) {
      option.access = currentUser.is_subscriber || currentUser.comped;
    }
    return option;
  });

  const handleSubmit = ({
    commentNotificationEnabled,
    sendForumDigest,
    sendPromotionEmails,
    sendEventEmails,
    sendProductEmails,
    surveyEnabled,
  }) => {
    const sanitizedParams = {
      user: {
        comment_notification_enabled: commentNotificationEnabled,
        send_forum_digest: sendForumDigest,
        send_promotion_emails: sendPromotionEmails,
        send_event_emails: sendEventEmails,
        send_product_emails: sendProductEmails,
        survey_enabled: surveyEnabled,
      },
    };
    updateEmailSettings(sanitizedParams);
    checkForConfirmationModal();
  };

  return (
    <div className={styles.notificationSettings}>
      <Formik
        onSubmit={handleSubmit}
        validationSchema={schema}
        initialValues={{
          commentNotificationEnabled: userEmailSettings.commentNotificationEnabled,
          sendForumDigest: userEmailSettings.sendForumDigest,
          sendPromotionEmails: userEmailSettings.sendPromotionEmails,
          sendEventEmails: userEmailSettings.sendEventEmails,
          sendProductEmails: userEmailSettings.sendProductEmails,
          surveyEnabled: userEmailSettings.surveyEnabled,
        }}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit}>
            <AutoSave debounceMs={500} />
            {NOTIFICATION_OPTIONS.filter((option) => option.access).map((option) => (
              <div className={styles.notificationContainer} key={option.description}>
                <CheckBoxField name={option.name} />
                <div className={styles.descriptionContainer}>
                  <div className={styles.title}>{option.title}</div>
                  <div className={styles.subheader}>{option.description}</div>
                </div>
              </div>
            ))}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default NotificationSettings;
